import { getCoachUnreadCount, queryKeys } from "@healthos/client-data";
import { capitalised } from "@healthos/shared";
import { IconProvider } from "@healthos/ui";
import type { DefaultNavItem } from "@healthos/ui/shell";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { client } from "../../api/healthos";
import { useDocumentTitle } from "../../hooks/use-document-title";
import { CareWordsContext } from "../../pages/coaching/care-words";
import { useProfessionalWords } from "../../pages/operations/coaching/use-professional-words";
import { useDashboard, useOperationsMe } from "../../pages/operations/dashboard/_hooks";
import { hasCap, type OperationsMe } from "../../pages/operations/dashboard/_shared";
import { OpsBottomNav } from "./ops-bottom-nav";
import { OpsChromeBar } from "./ops-chrome-bar";
import { OpsCompanyProvider } from "./ops-company-context";
import { OPS_NAV_ITEMS, OpsSideNav, opsNavItemsFor } from "./ops-side-nav";

// Longest path first so /operations/admin/integrations does not match /operations.
const OPS_TITLES = [...OPS_NAV_ITEMS].sort((a, b) => b.path.length - a.path.length);

function opsPageName(pathname: string): string {
  return OPS_TITLES.find((item) => pathname.startsWith(item.path))?.label ?? "Operations";
}

interface OpsShellProps {
  children: ReactNode;
  /** The way back to the member app, offered only to someone who has one. */
  companionExit: boolean;
}

/**
 * The operations boundary. Fails closed: nothing renders until the caller's
 * capabilities resolve, a 403 or a fetch failure denies the whole surface,
 * and the nav only shows areas the server will actually serve. Per-page
 * guards (RequireOpsCap) repeat the check at the route level so a deep link
 * cannot outrun the nav filtering.
 */
export function OpsShell({ children, companionExit }: OpsShellProps) {
  const meQuery = useOperationsMe();
  const navItems = useOpsNavItems(meQuery.data ?? null);
  const { pathname } = useLocation();
  useDocumentTitle(opsPageName(pathname));

  if (meQuery.isPending) {
    return (
      <div className="grid min-h-screen place-items-center bg-ambient-mesh">
        <div className="h-10 w-64 animate-pulse rounded-md bg-surface-track/50" aria-hidden />
      </div>
    );
  }

  const me = meQuery.data ?? null;
  const items = navItems;
  const home = items[0];
  if (meQuery.isError || !home) {
    return <OpsDenied />;
  }
  if (pathname === "/operations" || pathname === "/operations/") {
    return <Navigate to={home.path} replace />;
  }

  return (
    <IconProvider>
      <OpsCompanyProvider>
        <div className="min-h-screen bg-ambient-mesh">
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-navy focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-ink-on-brand focus:shadow-modal focus:outline-2 focus:outline-offset-2 focus:outline-teal-400"
          >
            Skip to content
          </a>
          <div className="md:flex md:min-h-screen">
            <OpsSideNav me={me} items={items} exit={companionExit} />
            <main
              id="main-content"
              tabIndex={-1}
              className="flex-1 pb-28 md:pb-0 min-h-screen relative flex flex-col focus:outline-none"
            >
              {hasCap("ops:view", me) ? <OpsChromeBar /> : null}
              <div className="w-full flex-1">
                <ProfessionalWords coach={hasCap("ops:coach", me)}>{children}</ProfessionalWords>
              </div>
            </main>
          </div>
          <OpsBottomNav items={items} />
        </div>
      </OpsCompanyProvider>
    </IconProvider>
  );
}

function OpsDenied({ scoped = false }: { scoped?: boolean }) {
  return (
    <div className="grid min-h-screen place-items-center bg-ambient-mesh p-8">
      <div className="max-w-md text-center">
        <p className="font-mono text-xs text-ink-tertiary uppercase tracking-wider">Operations</p>
        <h1 className="mt-2 font-semibold text-ink-primary text-xl">
          {scoped ? "Not in your role" : "No operations access"}
        </h1>
        <p className="mt-2 text-sm text-ink-secondary leading-relaxed">
          {scoped
            ? "Your operations seat does not include this area. If you need it, ask your HealthOS admin to widen the role."
            : "Your account has no operations seat. If you should have one, ask your HealthOS admin to assign the role."}
        </p>
      </div>
    </div>
  );
}

/** The nav the caller may use, with a coach's unread replies counted on Coachees. */
function useOpsNavItems(me: OperationsMe | null): DefaultNavItem[] {
  const coach = hasCap("ops:coach", me);
  const unreadQuery = useQuery({
    queryKey: queryKeys.coachUnread,
    queryFn: () => getCoachUnreadCount(client()),
    enabled: coach,
    refetchInterval: 60_000,
  });
  const words = useProfessionalWords(coach);
  const unread = unreadQuery.data ?? 0;
  return opsNavItemsFor(me).map((item) =>
    item.id === "coachees"
      ? {
          ...item,
          label: capitalised(words.members),
          ...(unread > 0 ? { badge: unread } : {}),
        }
      : item,
  );
}

/** A coach or clinician's pages speak their practice; a coachee's page sets its own programme's words over this. */
function ProfessionalWords({ coach, children }: { coach: boolean; children: ReactNode }) {
  const words = useProfessionalWords(coach);
  return <CareWordsContext.Provider value={words}>{children}</CareWordsContext.Provider>;
}

/** Route-level capability guard for pages inside the shell. */
export function RequireOpsCap({ cap, children }: { cap: string; children: ReactNode }) {
  const meQuery = useOperationsMe();
  if (meQuery.isPending) return null;
  if (meQuery.isError || !hasCap(cap, meQuery.data ?? null)) return <OpsDenied scoped />;
  return <>{children}</>;
}

/** Re-export point so the nav module can stay presentation-only. */
export { useDashboard };
